import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    LinearProgress,
    Link,
    Stack,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowsRotate, faClock, faPlus} from '@fortawesome/pro-solid-svg-icons';
import {useSnackbar} from 'notistack';
import {format, parseISO} from 'date-fns';

import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {CalendarAccessDto} from 'apiClient/Api';
import {settingsApi} from 'Pages/Admin/Settings/settingsApi';
import {apiErrorMessage} from 'Pages/Calendar/errorText';

import {LabelEmployee, LabelTask, LabelTaskList} from './labelTaskTypes';
import TaskEditDialog from './TaskEditDialog';
import TaskOrderDialog from './TaskOrderDialog';
import TimeEntryDialog from './TimeEntryDialog';

/** Der Status-Filter der Liste — dieselben Werte versteht der Server. */
type StatusFilter = 'offen' | 'erledigt' | 'alle';

const day = (iso: string): string => format(parseISO(iso), 'dd.MM.yyyy');

/**
 * Die eigenen Labelwin-Aufgaben.
 *
 * Die Person ist nie Teil der Anfrage — der Server leitet sie aus dem LabelBenutzer des Kontos ab
 * (dasselbe Prinzip wie beim Kalender). Ob die Anbindung überhaupt steht, sagt
 * `GET /Calendar/Me/Access`: Aufgaben und Kalender hängen an derselben Anbindung und derselben
 * Zuordnung, deshalb wird der vorhandene Zugriffs-Endpunkt mitbenutzt.
 *
 * Bearbeiten, Erledigen und Anlegen erscheinen nur, wenn der Schreibzugriff der Anbindung
 * eingeschaltet ist (`allowWrites`) — die Oberfläche verspricht nie mehr, als die API hält.
 */
export const LabelTasks: React.FC = () => {
    const {t} = useTranslation();
    useDocumentTitle('labelTasks.title');
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const canAssign = context.hasPermission('labelwin.tasks.assign');
    const canBookTime = context.hasPermission('labelwin.time.book');
    const canCreateOrder = context.hasPermission('labelwin.orders.create');

    const [access, setAccess] = useState<CalendarAccessDto | undefined>(undefined);
    const [accessLoading, setAccessLoading] = useState(true);
    const [list, setList] = useState<LabelTaskList | undefined>(undefined);
    const [status, setStatus] = useState<StatusFilter>('offen');
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const [employees, setEmployees] = useState<LabelEmployee[]>([]);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState<LabelTask | null>(null);
    /**
     * Auftragsnummer, für die der Zeitbuchungs-Dialog offen ist.
     * `undefined` = Dialog zu, `null` = offen mit frei eingebbarer Auftragsnummer.
     */
    const [timeOrder, setTimeOrder] = useState<string | null | undefined>(undefined);
    /** Die Aufgabe, aus der gerade ein Kundendienst-Auftrag werden soll. */
    const [orderTask, setOrderTask] = useState<LabelTask | null>(null);

    useEffect(() => {
        setAccessLoading(true);
        context.api.calendar
            .calendarGetMyAccess()
            .then((response) => setAccess(response.data))
            .catch(context.apiError)
            .finally(() => setAccessLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mitarbeiterliste nur für Zuweiser — alle anderen legen ohnehin nur für sich selbst an.
    useEffect(() => {
        if (!canAssign) return;
        http.get<LabelEmployee[]>('/Tasks/Employees')
            .then((r) => setEmployees(r.data))
            // Still: ohne Liste bleibt die Auswahl leer, eigene Aufgaben funktionieren weiter.
            .catch(() => setEmployees([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canAssign]);

    const load = useCallback(async () => {
        if (!access?.available) return;
        setLoading(true);
        setError(undefined);
        try {
            const r = await http.get<LabelTaskList>('/Tasks/Me', {status, limit: 200});
            setList(r.data);
        } catch (e) {
            setError(apiErrorMessage(e, t('labelTasks.loadFailed')));
            setList(undefined);
        } finally {
            setLoading(false);
        }
    }, [access?.available, http, status, t]);

    useEffect(() => {
        void load();
    }, [load]);

    /** Erledigen bzw. Wiederöffnen — ein PATCH mit genau einem Feld. */
    const setDone = async (task: LabelTask, done: boolean): Promise<void> => {
        setBusy(true);
        try {
            await http.patch<LabelTask>(`/Tasks/${task.id}`, {done});
            enqueueSnackbar(done ? t('labelTasks.completed') : t('labelTasks.reopened'),
                {variant: 'success'});
            await load();
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('labelTasks.saveFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    const writesAllowed = access?.allowWrites ?? false;

    const card = (task: LabelTask): React.ReactNode => (
        <Paper key={task.id} sx={{p: 2, opacity: task.done ? 0.75 : 1}}>
            <Stack direction="row" spacing={2}
                   sx={{alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1}}>
                <Box sx={{flexGrow: 1, minWidth: 240}}>
                    <Stack direction="row" spacing={1}
                           sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5}}>
                        <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                            {task.subject ?? '—'}
                        </Typography>
                        {task.priority > 0 && (
                            <Chip size="small" color="warning" variant="outlined"
                                  label={t('labelTasks.priorityChip', {priority: task.priority})}/>
                        )}
                        {task.activity && <Chip size="small" variant="outlined" label={task.activity}/>}
                        {task.orderNumber && (
                            <Chip size="small" variant="outlined" label={task.orderNumber}/>
                        )}
                        {task.done && (
                            <Chip size="small" color="success" variant="outlined"
                                  label={t('labelTasks.doneChip')}/>
                        )}
                    </Stack>

                    {(task.customerName || task.contactName || task.contactPhone) && (
                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                            {[
                                [task.customerName, task.customerCity].filter(Boolean).join(', '),
                                task.contactName,
                            ].filter(Boolean).join(' · ')}
                            {task.contactPhone && <>
                                {' · '}
                                <Link href={`tel:${task.contactPhone.replace(/[^+0-9]/g, '')}`}
                                      underline="hover">
                                    {task.contactPhone}
                                </Link>
                            </>}
                        </Typography>
                    )}

                    {task.text && (
                        <Typography variant="body2" sx={{mt: 0.5, whiteSpace: 'pre-wrap'}}>
                            {task.text}
                        </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.5}}>
                        {task.dueDate && `${t('labelTasks.dueOn', {date: day(task.dueDate)})} · `}
                        {task.createdBy && `${t('labelTasks.createdBy', {name: task.createdBy})}`}
                        {task.createdAt && ` · ${day(task.createdAt)}`}
                        {task.done && task.completedAt &&
                            ` — ${t('labelTasks.completedOn', {date: day(task.completedAt)})}`}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} sx={{flexShrink: 0}}>
                    {/*
                      * Ohne Auftrag lässt sich keine Zeit buchen — Labelwin kennt Zeiten nur am
                      * Auftrag. Deshalb steht hier der Weg dorthin, statt einen Knopf anzubieten,
                      * der nur mit einer Erklärung endet.
                      */}
                    {canCreateOrder && writesAllowed && !task.done && !task.orderNumber && (
                        <Button size="small" variant="outlined" disabled={busy}
                                onClick={() => setOrderTask(task)}>
                            {t('labelTasks.createOrder')}
                        </Button>
                    )}
                    {canBookTime && task.orderNumber && (
                        <Button size="small" variant="text" disabled={busy}
                                onClick={() => setTimeOrder(task.orderNumber!)}>
                            {t('timeTracking.book')}
                        </Button>
                    )}
                    {writesAllowed && !task.done && (
                        <Button size="small" variant="text" disabled={busy}
                                onClick={() => { setEditing(task); setEditorOpen(true); }}>
                            {t('labelTasks.edit')}
                        </Button>
                    )}
                    {writesAllowed && task.done && (
                        <Button size="small" variant="text" disabled={busy}
                                onClick={() => void setDone(task, false)}>
                            {t('labelTasks.reopen')}
                        </Button>
                    )}
                    {writesAllowed && !task.done && (
                        <Button size="small" variant="contained" disabled={busy}
                                onClick={() => void setDone(task, true)}>
                            {t('labelTasks.complete')}
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Paper>
    );

    if (accessLoading) {
        return (
            <Grid size={12}>
                <Box sx={{display: 'flex', justifyContent: 'center', p: 6}}><CircularProgress/></Box>
            </Grid>
        );
    }

    if (!access?.available) {
        return (
            <Grid size={12} sx={{maxWidth: 900}}>
                <Typography variant="h5" gutterBottom>{t('labelTasks.title')}</Typography>
                <Alert severity={access?.hasLabelUser === false ? 'info' : 'warning'} sx={{mt: 2}}>
                    <AlertTitle>{t('labelTasks.unavailableTitle')}</AlertTitle>
                    {access?.message}
                </Alert>
            </Grid>
        );
    }

    return (
        <Grid size={12}>
            <Typography variant="h5" gutterBottom>{t('labelTasks.title')}</Typography>

            <Stack direction="row" spacing={1}
                   sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1, mb: 2}}>
                {writesAllowed && (
                    <Button variant="contained" startIcon={<FontAwesomeIcon icon={faPlus}/>}
                            onClick={() => { setEditing(null); setEditorOpen(true); }}>
                        {t('labelTasks.new')}
                    </Button>
                )}
                {/* Freie Buchung ohne Aufgabenbezug — die Auftragsnummer wird im Dialog eingegeben. */}
                {canBookTime && (
                    <Button variant="outlined" startIcon={<FontAwesomeIcon icon={faClock}/>}
                            onClick={() => setTimeOrder(null)}>
                        {t('timeTracking.book')}
                    </Button>
                )}
                <Button variant="text" startIcon={<FontAwesomeIcon icon={faArrowsRotate}/>}
                        onClick={() => void load()} disabled={loading}>
                    {t('labelTasks.reload')}
                </Button>
                <Box sx={{flexGrow: 1}}/>
                {!writesAllowed && (
                    <Tooltip title={access.message ?? ''}>
                        <Chip size="small" variant="outlined" label={t('labelTasks.readOnly')}/>
                    </Tooltip>
                )}
                {list && (
                    <Chip size="small" label={t('labelTasks.counts', {open: list.open, done: list.done})}/>
                )}
                <ToggleButtonGroup exclusive size="small" value={status}
                                   onChange={(_, next) => next && setStatus(next as StatusFilter)}>
                    <ToggleButton value="offen">{t('labelTasks.filter.open')}</ToggleButton>
                    <ToggleButton value="erledigt">{t('labelTasks.filter.done')}</ToggleButton>
                    <ToggleButton value="alle">{t('labelTasks.filter.all')}</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {loading && <LinearProgress sx={{mb: 2}}/>}
            {error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(undefined)}>{error}</Alert>}

            {!loading && !error && list && list.tasks.length === 0 && (
                <Alert severity="success" sx={{mb: 2}}>{t('labelTasks.empty')}</Alert>
            )}

            <Stack spacing={1.5}>
                {list?.tasks.map(card)}
            </Stack>

            <TaskEditDialog
                open={editorOpen}
                task={editing}
                employees={employees}
                canAssign={canAssign}
                onClose={() => setEditorOpen(false)}
                onSaved={() => void load()}/>

            <TimeEntryDialog
                open={timeOrder !== undefined}
                orderNumber={timeOrder ?? null}
                onClose={() => setTimeOrder(undefined)}/>

            <TaskOrderDialog
                task={orderTask}
                onClose={() => setOrderTask(null)}
                onCreated={() => void load()}/>
        </Grid>
    );
};

export default LabelTasks;
